import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
// Same module instance as app imports (`…/language/dico`) - avoid `.js` suffix drift
import dico, {
  type AppLanguage,
  getAppLanguage,
  setAppLanguage,
  subscribeLanguage,
} from "../../../util/src/language/dico";

type Dictionary = ReturnType<typeof dico>;

type LanguageContextValue = {
  /** Short language code currently active. */
  language: AppLanguage;
  /** Apply a new language (notifies all subscribers). */
  setLanguage: (language: AppLanguage | string) => void;
  /** Dictionary for the active language (recomputed on change). */
  dico: Dictionary;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

type LanguageProviderProps = {
  children: React.ReactNode;
  /** Optional initial language applied on mount before subscriptions. */
  initialLanguage?: AppLanguage | string;
};

/**
 * React bridge over the module-level app language in `dico.ts`.
 * Forces a re-render when `setAppLanguage` is called from anywhere
 * (Electron store, Socket.IO hub, remote gateway).
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  initialLanguage,
}) => {
  const [language, setLanguageState] = useState<AppLanguage>(() => {
    if (initialLanguage !== undefined) {
      return setAppLanguage(initialLanguage);
    }
    return getAppLanguage();
  });
  // Keep module language in sync if the provider mounts with a new initial value
  useEffect(() => {
    if (initialLanguage === undefined) {
      return;
    }
    setLanguageState(setAppLanguage(initialLanguage));
  }, [initialLanguage]);
  // Subscribe to external language changes (hub, other windows, etc.)
  useEffect(() => {
    return subscribeLanguage((next) => {
      setLanguageState(next);
    });
  }, []);
  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage: (next) => {
        setLanguageState(setAppLanguage(next));
      },
      dico: dico(),
    }),
    [language],
  );
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Access the reactive app language and dictionary.
 * Must be used under LanguageProvider.
 */
export const useAppLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Fallback for call sites outside the provider: still return live dico
    const language = getAppLanguage();
    return {
      language,
      setLanguage: (next) => {
        setAppLanguage(next);
      },
      dico: dico(),
    };
  }
  return ctx;
};

export default LanguageProvider;
